import * as Sentry from "@sentry/react";
import cx from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAFForm } from "../hooks/useAFForm";
import { getCurrentQuestion, getPreviousQuestion, setCurrentPageProgress, skipCurrentQuestion, submitAnswer } from "../api/ApiAppForm";
import AFCannotContinue from "../components/AFCannotContinue";
import AFLayout from "../components/AFLayout";
import AFMultiForms from "../components/AFMultiForms";
import AppQuestionForm from "../components/AppQuestionForm";
import AFQuestionPlaceholder from "../components/placeholders/AFQuestionPlaceholder";
import { PageType } from "../enums/PageType";
import { IAFQuestionData, IAppFormQuestion, IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AppForm.module.scss";
import {
    getQuestionID,
    getQuestionLabel,
    getQuestionType,
    redirectTo404Page
} from "../shared/AppFormHelper";

import { ViewContext } from "../providers/ViewStateProvider";
import { unstable_batchedUpdates } from "react-dom";

type AppFormParams = {
    applicantToken: string;
};

const AppForm: React.FC = () => {
    const { setError } = useContext(ViewContext);

    const history = useHistory();
    const [isFormReady, setIsFormReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentQuestionList, setCurrentQuestionList] = useState<IAppFormQuestion[]>([null]);
    const params = useParams<AppFormParams>();
    const applicantToken = params.applicantToken;
    const [isMultipleQuestion, setIsMultipleQuestion] = useState(false);
    const [noAvailableQuestion, setNoAvailableQuestion] = useState(false);
    const [formQuestionLabel, setFormQuestionLabel] = useState("");
    const [formQuestionHelpText, setFormQuestionHelpText] = useState("");
    const [formQuestionType, setFormQuestionType] = useState("");
    const [isHalted, setIsHalted] = useState(false);
    const [haltedReasons, setHaltedReasons] = useState<string[]>([]);

    const { actions, selectors } = useContext(AppFormControllerContext);

    const applicant = selectors.getApplicantFromToken(applicantToken);
    if (!applicant || !applicant?.applicationId) {
        redirectTo404Page();
        return;
    }

    const {
        getCurrentPage,
        navigateBackward
    } = useAFForm({ pageType: PageType.AppForm, currentClientNumber: applicant?.clientNumber ?? 1 });


    const checkBackQuestion = useCallback((info): void => {
        if (!info?.questions?.length) return;
        const questionInfo = currentQuestionList[0];
        if (getQuestionID(info?.questions[0]) === getQuestionID(questionInfo)) {
            // Navigate backwards if this question is the first question
            const applicantList = selectors.getApplicantList();
            const currentApplicant = applicantList.find((i) => i.applicantToken === applicantToken);
            navigateBackward(currentApplicant);
            return;
        }
        updateQuestionState(info.questions);
        actions.setQuestionResponse(info.response);
    }, [currentQuestionList]);


    const updateQuestionState = useCallback((response: IAppFormQuestion[]): void => {
        unstable_batchedUpdates(() => { 
            setIsMultipleQuestion(response?.length > 1);
            setNoAvailableQuestion(false);
            if (response?.length > 1) {
                const firstQuestion = response[0];
                setCurrentQuestionList([...response]);
                setFormQuestionLabel(getQuestionLabel(firstQuestion));
                setFormQuestionHelpText(firstQuestion?.questionHeaderHelp)
                setFormQuestionType(getQuestionType(firstQuestion));
                setIsFormReady(true);
                return;
            } else if (response?.length) {
                setCurrentQuestionList([{ ...response[0] }]);
                setFormQuestionLabel("");
                setIsFormReady(true);
                return;
            }
            setNoAvailableQuestion(true);
        });
    }, []);


    // Actually transitions to the next page (of questions, or page in the form flow)
    const gotoNextPage = useCallback((res: IAFQuestionData): void => {
        if (res?.halt) {
            setIsHalted(true);
            setHaltedReasons(res?.haltReasons);
            return;
        }

        if (res?.complete) {
            setIsFormReady(false);
            const applicants = selectors.getApplicantList();
            const nextApplicant = applicants.find((i) => i.clientNumber === applicant.clientNumber + 1);
            if (!nextApplicant) {
                const pageInfo: IPageInfo = {
                    pageType: PageType.Declarations,
                    currentClientNumber: 1
                }
                setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
                )
                history.push(`/app-form/details/${applicantToken}/declarations`);
                return;
            }
            const pageInfo: IPageInfo = {
                pageType: PageType.AppForm,
                currentClientNumber: nextApplicant.clientNumber
            }
            setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
            )
            history.push(`/app-form/details/${nextApplicant.applicantToken}`);
        }

        if (res?.isSuccessful) {
            updateQuestionState(res.questions);
            actions.setQuestionResponse(res.response);
        } else {
            setIsFormReady(true);
        }
    }, [applicant]);


    // Could be next page of questions, or next page (daclarations or next applicant)
    const requestNextPage = useCallback((params): void => {
        const paramObj = {
            answer: {
                answers: params
            }
        };
        setIsSubmitting(true);
        setIsFormReady(false);
        actions.setQuestionResponse(null);
        submitAnswer(applicantToken, paramObj)
            .then((res) => {
                gotoNextPage(res);
            })
            .catch(() => {
                setError("There was a problem saving your response.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicantToken ${applicantToken} on the Insurer questions page. There was a problem submitting the answer.`, "error");
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [applicantToken, gotoNextPage]);


    const requestPreviousPage = useCallback((e): void => {
        e.preventDefault();
        setIsFormReady(false);
        getPreviousQuestion(applicantToken)
            .then((res) => {
                checkBackQuestion(res);
            })
            .catch(() => {
                setError("There was a problem retrieving the previous question set.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicantToken ${applicantToken} on the Insurer questions page. There was a problem retrieving the previous question set.`, "error");
                }
            })
            .finally(() => setIsFormReady(true));
    }, [applicantToken, checkBackQuestion]);


    const requestSkip = useCallback((): void => {
        setIsFormReady(false);
        skipCurrentQuestion(applicantToken)
            .then((res) => {
                const applicants = selectors.getApplicantList();
                const newApplicantList = applicants.map((a) =>
                    a === applicant ?
                        {
                            ...a,
                            hasAcknowledgedSkipDialog: true
                        }
                        : a
                );
                actions.setApplicantList(newApplicantList);
                gotoNextPage(res);
            })
            .catch(() => {
                setError("There was a problem skipping the current set of questions.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicantToken ${applicantToken} on the Insurer questions page. There was a problem skipping the current set of questions.`, "error");
                }
            })
            .finally(() => setIsFormReady(true));
    }, [applicantToken, applicant, gotoNextPage]);


    const getApplicantName = useCallback((): string => {
        var name = selectors.getApplicantName(applicantToken);
        return name? `${name}:` : "";
    }, [applicantToken]);


    const keyDownHandler = useCallback((event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            requestPreviousPage(event);
        }
    }, [requestPreviousPage]);


    useEffect(() => {
        setIsFormReady(false);
        getCurrentQuestion(applicantToken)
            .then((res) => {
                if (res?.halt) {
                    setIsHalted(true);
                    setHaltedReasons(res?.haltReasons);
                    return;
                }
                if (!res?.questions?.length) {
                    setError("There was a problem retrieving the current set of questions.");
                    if (typeof Sentry !== 'undefined') {
                        Sentry.captureMessage(`Error occured for ApplicantToken ${applicantToken} on the Insurer questions page. There was a problem retrieving the current set of questions.`, "error");
                    }
                    return;
                }
                updateQuestionState(res.questions);
                actions.setQuestionResponse({ ...res.response });
            })
            .catch(() => {
                setError("There was a problem retrieving the current set of questions.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicantToken ${applicantToken} on the Insurer questions page. There was a problem retrieving the current set of questions.`, "error");
                }
            });
    }, [applicantToken]);

    useEffect(() => {
        document.addEventListener("keydown", keyDownHandler);
        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, [keyDownHandler]);


    return (
        <AFLayout currentPage={getCurrentPage()}>
            <div className={cx(styles["advice-progress"], styles["layout"])}>
                {isHalted ? (
                    <AFCannotContinue
                        reason={haltedReasons}
                    />
                ) : (
                    <>
                        {(!isFormReady || isSubmitting) && <AFQuestionPlaceholder />}
                        {isFormReady &&
                            !isSubmitting &&
                            !isMultipleQuestion &&
                            !noAvailableQuestion && (
                                <AppQuestionForm
                                    applicantName={getApplicantName()}
                                    question={currentQuestionList[0]}
                                    nextQuestion={requestNextPage}
                                    handleBack={requestPreviousPage}
                                    skipIsVisible={true}
                                    handleSkip={requestSkip}
                                    showSkipDialog={!selectors.getApplicantList().find((i) => i.applicantToken === applicantToken).hasAcknowledgedSkipDialog}
                                />
                            )}

                        {isFormReady &&
                            !isSubmitting &&
                            isMultipleQuestion &&
                            currentQuestionList?.length &&
                            !noAvailableQuestion && (
                                <AFMultiForms
                                    applicantName={getApplicantName()}
                                    list={currentQuestionList}
                                    nextQuestion={requestNextPage}
                                    formLabel={formQuestionLabel}
                                    helpText={formQuestionHelpText}
                                    formQuestionType={formQuestionType}
                                    handleBack={requestPreviousPage}
                                    skipIsVisible={true}
                                    handleSkip={requestSkip}
                                    showSkipDialog={!selectors.getApplicantList().find((i) => i.applicantToken === applicantToken).hasAcknowledgedSkipDialog}
                                />
                            )}
                    </>
                )}
            </div>
        </AFLayout>
    );
};

export default AppForm;
